/**
 * `OLE2FRAME` entity → `RenderableEntity` adapter (TYPED).
 *
 * # The frame is what AutoCAD draws when it cannot draw the object
 *
 * An OLE object's payload is an OLE compound document — for the common
 * "Picture (Enhanced Metafile)" case, an EMF. Rasterising EMF needs a
 * metafile interpreter this project does not have, so the embedded picture is
 * DELIBERATELY not drawn. What IS drawn is the frame rectangle between the
 * entity's two WCS corners, which is precisely AutoCAD's own documented
 * behaviour when the OLE server is unavailable — the degraded state of the
 * real application, not a stand-in invented here.
 *
 * Read this as an explicit boundary, not as a finished OLE implementation:
 * the picture inside the frame is missing and stays missing until an EMF
 * rasteriser exists. Everything else about the entity is real — it takes part
 * in bounds, the spatial index, picking, selection, layer visibility and
 * properties like any other geometry, and the payload round-trips untouched.
 *
 * The frame is a CLOSED 4-corner polyline, so it renders through the same
 * `polyline` scene geometry every other closed outline uses; no new renderer
 * capability is involved.
 */

import type { OleFrameEntity } from "@dxf-ai-editor/drawing-model";

import { applyOffset } from "../../coordinateOrigin";
import type { RenderableEntity, SceneGeometry, Vec3 } from "../../types";

import {
  boundsFromPoints,
  buildRenderableShell,
  type AdapterContext,
} from "../adapterContext";

/**
 * The frame's FOUR rectangle corners in RAW (un-offset) WCS, in drawing order
 * — upper-left → upper-right → lower-right → lower-left — or `null` when a
 * stored corner is non-finite (a frame that cannot be described is dropped
 * rather than poisoning the scene AABB with NaN, the same guard the other
 * typed adapters apply to degenerate input).
 *
 * PUBLIC on purpose (@codex PR #785 round 2, F1). `OLE2FRAME` stores only TWO
 * opposite corners and no rotation, so anything that has to place the frame
 * under an arbitrary affine — the block-child render arm
 * (`./blockChildOleFrame.ts`) and the PDF plot converter — must map these four
 * corners itself; mapping the two stored ones reports the image of the
 * DIAGONAL instead of the rectangle. One definition of "which four points is
 * this frame", so those consumers cannot disagree with what the screen draws.
 */
export function oleFrameCorners(
  entity: Pick<OleFrameEntity, "upperLeft" | "lowerRight">,
): [Vec3, Vec3, Vec3, Vec3] | null {
  const a = entity.upperLeft;
  const b = entity.lowerRight;
  const az = a.z ?? 0;
  const bz = b.z ?? 0;
  if (!Number.isFinite(a.x + a.y + az) || !Number.isFinite(b.x + b.y + bz)) {
    return null;
  }
  return [
    { x: a.x, y: a.y, z: az },
    { x: b.x, y: a.y, z: az },
    { x: b.x, y: b.y, z: bz },
    { x: a.x, y: b.y, z: bz },
  ];
}

/**
 * Build the frame renderable from FOUR already-placed RAW-WCS corners — the
 * shared tail of the top-level adapter and the block-child arm
 * (`./blockChildOleFrame.ts`), which differ only in how the corners were
 * placed. The corners are offset once here (the single `applyOffset` every
 * typed adapter applies) and `bounds` is their exact AABB.
 *
 * The quad is drawn as authored: under an oblique block rotation it is a
 * ROTATED rectangle, not the axis-aligned box around it.
 */
export function oleFrameRenderableFromCorners(
  entity: OleFrameEntity,
  ctx: AdapterContext,
  rawCorners: readonly Vec3[],
): RenderableEntity {
  const corners = rawCorners.map(applyOffset);
  const geometry: SceneGeometry = {
    kind: "polyline",
    points: corners,
    closed: true,
  };
  return buildRenderableShell(entity, ctx, geometry, boundsFromPoints(corners));
}

/** Typed adapter for the `OLE Frame` entity (`oleFrameEntitySchema`). */
export function oleFrameFromEntity(
  entity: OleFrameEntity,
  ctx: AdapterContext,
): RenderableEntity | null {
  const corners = oleFrameCorners(entity);
  if (!corners) return null;
  return oleFrameRenderableFromCorners(entity, ctx, corners);
}
